import React, { useRef, useState, useCallback } from 'react';

const isSecure = window.isSecureContext || window.location.hostname === 'localhost';

interface PitchResult { frequency: number; clarity: number; hasVoice: boolean; note: string; }
interface ScoreState { total: number; singing: number; totalFrames: number; pitchAccuracy: number; currentStreak: number; maxStreak: number; }

const createScore = (): ScoreState => ({ total: 0, singing: 0, totalFrames: 0, pitchAccuracy: 0, currentStreak: 0, maxStreak: 0 });

const detectPitch = (buffer: Float32Array, sampleRate: number): PitchResult => {
  const SIZE = buffer.length;
  const MAX = Math.floor(SIZE / 2);
  let rms = 0;
  for (let i = 0; i < SIZE; i++) rms += buffer[i] * buffer[i];
  rms = Math.sqrt(rms / SIZE);
  if (rms < 0.01) return { frequency: 0, clarity: 0, hasVoice: false, note: '' };
  const corr = new Float32Array(MAX);
  for (let i = 0; i < MAX; i++) { let s = 0; for (let j = 0; j < MAX; j++) s += buffer[j] * buffer[j + i]; corr[i] = s; }
  let d = 0;
  while (d < MAX && corr[d] > corr[d + 1]) d++;
  let maxVal = -1, maxPos = -1;
  for (let i = d; i < MAX; i++) { if (corr[i] > maxVal) { maxVal = corr[i]; maxPos = i; } }
  if (maxPos < 0) return { frequency: 0, clarity: 0, hasVoice: true, note: '' };
  const clarity = maxVal / corr[0];
  const freq = sampleRate / maxPos;
  if (freq < 80 || freq > 1100 || clarity < 0.9) return { frequency: 0, clarity: 0, hasVoice: true, note: '' };
  return { frequency: freq, clarity, hasVoice: true, note: '' };
};

const updateScore = (state: ScoreState, pitch: PitchResult): ScoreState => {
  const s = { ...state };
  s.totalFrames++;
  if (!pitch.hasVoice) return s;
  s.singing++;
  if (pitch.clarity > 0.92) {
    s.currentStreak++; s.maxStreak = Math.max(s.maxStreak, s.currentStreak);
    s.pitchAccuracy = (s.pitchAccuracy * (s.singing - 1) + 100) / s.singing;
  } else {
    s.currentStreak = Math.max(0, s.currentStreak - 1);
    s.pitchAccuracy = (s.pitchAccuracy * (s.singing - 1) + 50) / s.singing;
  }
  const participation = Math.min(100, (s.singing / s.totalFrames) * 150);
  s.total = Math.round(participation * 0.4 + s.pitchAccuracy * 0.4 + Math.min(100, s.maxStreak * 2) * 0.2);
  return s;
};

const getLabel = (score: number) => {
  if (score >= 95) return { label: 'PERFECTO', color: '#f59e0b', emoji: '🌟' };
  if (score >= 85) return { label: 'EXCELENTE', color: '#10b981', emoji: '⭐' };
  if (score >= 70) return { label: 'MUY BIEN', color: '#3b82f6', emoji: '👏' };
  if (score >= 55) return { label: 'BIEN', color: '#8b5cf6', emoji: '👍' };
  return { label: 'SIGUE PRACTICANDO', color: '#f59e0b', emoji: '💪' };
};

interface Props {
  song: any; streamToken: string; apiUrl: string;
  userId?: string; token?: string;
  onFinished: () => void;
}

export default function VideoPlayer({ song, streamToken, apiUrl, userId, token, onFinished }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animRef = useRef<number>(0);
  const bufferRef = useRef<Float32Array>(new Float32Array(2048));
  const scoreRef = useRef<ScoreState>(createScore());
  const startRef = useRef<number>(0);

  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [score, setScore] = useState<ScoreState>(createScore());
  const [pitch, setPitch] = useState<PitchResult>({ frequency: 0, clarity: 0, hasVoice: false, note: '' });
  const [micOn, setMicOn] = useState(false);
  const [showResult, setShowResult] = useState(false);

  const analyze = useCallback(() => {
    if (!analyserRef.current || !audioCtxRef.current) return;
    analyserRef.current.getFloatTimeDomainData(bufferRef.current);
    const p = detectPitch(bufferRef.current, audioCtxRef.current.sampleRate);
    setPitch(p);
    if (videoRef.current && !videoRef.current.paused) {
      scoreRef.current = updateScore(scoreRef.current, p);
      setScore({ ...scoreRef.current });
    }
    animRef.current = requestAnimationFrame(analyze);
  }, []);

  const startMic = async () => {
    if (!isSecure) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const ctx = new AudioContext();
      audioCtxRef.current = ctx;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      analyserRef.current = analyser;
      ctx.createMediaStreamSource(stream).connect(analyser);
      bufferRef.current = new Float32Array(analyser.fftSize);
      animRef.current = requestAnimationFrame(analyze);
      setMicOn(true);
    } catch { setMicOn(false); }
  };

  const saveScore = async (finalScore: ScoreState) => {
    if (!userId || !token) return;
    try {
      await fetch(`${apiUrl}/api/scores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          song_id: song.id, player_name: 'Cliente web',
          score: finalScore.total, accuracy: finalScore.pitchAccuracy,
          duration_played: Math.floor((Date.now() - startRef.current) / 1000),
          mode: 'web',
        }),
      });
    } catch {}
  };

  const handleFinish = async () => {
    setPlaying(false);
    cancelAnimationFrame(animRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    await saveScore(scoreRef.current);
    setShowResult(true);
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) { v.pause(); setPlaying(false); }
    else {
      if (startRef.current === 0) { startRef.current = Date.now(); startMic(); }
      v.play(); setPlaying(true);
    }
  };

  const fmt = (s: number) => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(Math.floor(s%60)).padStart(2,'0')}`;
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const { label, color: sColor, emoji } = getLabel(score.total);

  if (showResult) {
    return (
      <div style={{ minHeight:'100vh', background:'#0a0a1a', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'sans-serif' }}>
        <div style={{ background:'#1a1a2e', borderRadius:'20px', padding:'2.5rem', textAlign:'center', maxWidth:'400px', width:'90%', border:'1px solid #2a2a3e' }}>
          <div style={{ fontSize:'4rem', marginBottom:'0.5rem' }}>{emoji}</div>
          <div style={{ fontSize:'5rem', fontWeight:900, fontFamily:'monospace', color:sColor, lineHeight:1 }}>{score.total}</div>
          <div style={{ fontSize:'1.2rem', fontWeight:700, color:sColor, letterSpacing:'2px', marginBottom:'1.5rem' }}>{label}</div>
          <div style={{ color:'#fff', fontWeight:700 }}>{song.title}</div>
          <div style={{ color:'#a78bfa', fontSize:'0.9rem', marginBottom:'2rem' }}>{song.artist}</div>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem', marginBottom:'2rem' }}>
            {[
              { label:'Participación', value: score.totalFrames > 0 ? Math.min(100, Math.round(score.singing/score.totalFrames*150)) : 0, color:'#7C3AED' },
              { label:'Afinación', value: Math.round(score.pitchAccuracy), color:'#10b981' },
              { label:'Mejor racha', value: Math.min(100, score.maxStreak*2), color:'#f59e0b' },
            ].map(b => (
              <div key={b.label} style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                <span style={{ color:'#888', fontSize:'0.8rem', width:'100px', textAlign:'right' }}>{b.label}</span>
                <div style={{ flex:1, height:'8px', background:'#0f0f1a', borderRadius:'99px', overflow:'hidden' }}>
                  <div style={{ width:`${b.value}%`, height:'100%', background:b.color, borderRadius:'99px' }} />
                </div>
                <span style={{ color:'#fff', fontSize:'0.85rem', width:'30px' }}>{b.value}</span>
              </div>
            ))}
          </div>
          <button onClick={onFinished} style={{ width:'100%', padding:'1rem', background:'#7C3AED', border:'none', borderRadius:'12px', color:'#fff', fontSize:'1rem', fontWeight:700, cursor:'pointer' }}>
            🎵 Cantar otra
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height:'100vh', background:'#000', display:'flex', flexDirection:'column', fontFamily:'sans-serif' }}>
      {/* Score overlay */}
      <div style={{ position:'absolute', top:'1rem', right:'1rem', zIndex:10, textAlign:'center' }}>
        <div style={{ background:'rgba(0,0,0,0.7)', borderRadius:'10px', padding:'0.5rem 0.75rem', backdropFilter:'blur(10px)' }}>
          <div style={{ fontSize:'2rem', fontWeight:900, fontFamily:'monospace', color:sColor, lineHeight:1 }}>{score.total}</div>
          <div style={{ fontSize:'0.65rem', color:sColor, letterSpacing:'1px' }}>{label}</div>
        </div>
        <div style={{ background:'rgba(0,0,0,0.7)', borderRadius:'8px', padding:'0.3rem 0.6rem', marginTop:'0.35rem', display:'flex', alignItems:'center', gap:'0.4rem' }}>
          <div style={{ width:'8px', height:'8px', borderRadius:'50%', background: pitch.hasVoice ? (pitch.clarity > 0.92 ? '#10b981' : '#f59e0b') : '#444', flexShrink:0 }} />
          <span style={{ fontSize:'0.7rem', color:'#888' }}>
            {!isSecure ? 'Sin mic (HTTP)' : micOn ? (pitch.hasVoice ? pitch.note || 'Cantando' : 'Escuchando') : 'Sin mic'}
          </span>
        </div>
      </div>

      <div style={{ flex:1, position:'relative', overflow:'hidden' }}>
        <video
          ref={videoRef}
          src={`${apiUrl}/songs/stream/${streamToken}/video`}
          onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime || 0)}
          onLoadedMetadata={e => setDuration((e.target as HTMLVideoElement).duration)}
          onEnded={handleFinish}
          preload="auto"
          style={{ width:'100%', height:'100%', objectFit:'contain' }}
          playsInline
        />
        {!playing && (
          <div onClick={togglePlay} style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(0,0,0,0.5)', cursor:'pointer' }}>
            <div style={{ width:'70px', height:'70px', borderRadius:'50%', background:'rgba(124,58,237,0.9)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.8rem', color:'#fff' }}>▶</div>
          </div>
        )}
      </div>

      <div style={{ padding:'0.75rem 1.5rem 1.5rem', background:'rgba(0,0,0,0.9)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', marginBottom:'0.75rem' }}>
          <span style={{ color:'#888', fontSize:'0.75rem', fontFamily:'monospace' }}>{fmt(currentTime)}</span>
          <div style={{ flex:1, height:'4px', background:'#333', borderRadius:'99px', cursor:'pointer' }} onClick={e => {
            const r = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
            if (videoRef.current) videoRef.current.currentTime = ((e.clientX-r.left)/r.width)*duration;
          }}>
            <div style={{ height:'100%', background:'#7C3AED', borderRadius:'99px', width:`${progress}%`, transition:'width 0.5s linear' }} />
          </div>
          <span style={{ color:'#888', fontSize:'0.75rem', fontFamily:'monospace' }}>{fmt(duration)}</span>
        </div>
        <div style={{ display:'flex', gap:'0.75rem', justifyContent:'center', alignItems:'center' }}>
          <div style={{ flex:1 }}>
            <div style={{ color:'#fff', fontWeight:600, fontSize:'0.9rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{song.title}</div>
            <div style={{ color:'#a78bfa', fontSize:'0.82rem' }}>{song.artist}</div>
          </div>
          <button onClick={togglePlay} style={{ width:'50px', height:'50px', borderRadius:'50%', background:'#7C3AED', border:'none', color:'#fff', fontSize:'1.3rem', cursor:'pointer', flexShrink:0 }}>
            {playing ? '⏸' : '▶'}
          </button>
          <button onClick={handleFinish} style={{ padding:'0 1rem', height:'50px', borderRadius:'10px', background:'#2a2a3e', border:'1px solid #444', color:'#fff', cursor:'pointer' }}>
            ⏹
          </button>
        </div>
      </div>
    </div>
  );
}
